import LockIcon from '@mui/icons-material/Lock';
import LockOpenOutlinedIcon from '@mui/icons-material/LockOpenOutlined';
import { CircularProgress, Typography } from '@mui/material';
import { EnhancedCard } from 'components/enhanced/card/EnhancedCard';
import { CheckboxAdapter } from 'components/enhanced/form/adapters/CheckboxAdapter';
import { DatePickerAdapter } from 'components/enhanced/form/adapters/date-time/DatePickerAdapter';
import { TextFieldAdapter } from 'components/enhanced/form/adapters/text-inputs/TextFieldAdapter';
import EnhancedForm from 'components/enhanced/form/EnhancedForm';
import { IdentityField } from 'components/enhanced/form/fields/IdentityField';
import { currencyFormatter } from 'components/helpers/currency-formatter';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { IEquipmentInvoice } from '../../types';

interface EquipmentInvoiceUpdateFormProps {
  invoice: IEquipmentInvoice;
  updateInvoice: (values: any) => void | Promise<any>;
}

export const EquipmentInvoiceUpdateForm = ({
  invoice,
  updateInvoice
}: EquipmentInvoiceUpdateFormProps) => {
  const { t } = useTranslation();
  const prefix = 'pages.equipment-invoices.detail';

  if (!invoice) {
    return (
      <div className="flex items-center justify-center p-8">
        <CircularProgress size={24} />
        <span className="ml-2 text-sm text-gray-500">{t('global.loading', 'Caricamento...')}</span>
      </div>
    );
  }

  return (
    <EnhancedForm
      onSubmit={updateInvoice}
      hideBackResetButton={true}
      submitLabel={t('actions.save')}
      formId="amountForm"
      autoStyleLayout={true}
      columns={2}>
      {invoice?.amount && (
        <EnhancedCard title={t(`entities.ddt.amount`)}>
          <Typography variant="h5" color="primary.main" fontWeight="bold">
            {currencyFormatter(invoice.amount)}
          </Typography>
        </EnhancedCard>
      )}
      {invoice?.expected_amount && (
        <EnhancedCard title={t(`${prefix}.invoice-expected`)}>
          <Typography variant="h5" color="text.primary" fontWeight="bold">
            {currencyFormatter(invoice.expected_amount)}
          </Typography>
        </EnhancedCard>
      )}
      <IdentityField
        name="ref"
        label={t(`entities.ddt.invoice_ref`)}
        initialValue={invoice.ref}
        component={TextFieldAdapter}
        disabled={invoice.closed}
      />
      <IdentityField
        name="date"
        label={t(`entities.ddt.invoice_date`)}
        component={DatePickerAdapter}
        initialValue={dayjs(invoice.date, 'DD/MM/YYYY').format('YYYY-MM-DD')}
        disabled={invoice.closed}
      />
      <IdentityField
        name="credit_memo_ref"
        label={t(`entities.ddt.credit_memo_ref`)}
        initialValue={invoice.credit_memo_ref}
        component={TextFieldAdapter}
        type="text"
        disabled={+invoice.credit_memo_amount === 0}
      />

      <IdentityField
        name="credit_memo_amount"
        label={t(`entities.ddt.credit_memo_amount`)}
        initialValue={currencyFormatter(invoice.credit_memo_amount)}
        component={TextFieldAdapter}
        type="text"
        disabled
      />
      <IdentityField
        name="credit_memo_date"
        label={t(`entities.ddt.credit_memo_date`)}
        component={TextFieldAdapter}
        initialValue={invoice.credit_memo_date}
        disabled
      />
      <IdentityField
        name="closed"
        label={invoice?.closed ? t(`entities.ddt.closed`) : t(`entities.ddt.open`)}
        component={CheckboxAdapter}
        type="checkbox"
        initialValue={invoice?.closed ?? false}
        icon={<LockOpenOutlinedIcon fontSize="large" />}
        checkedIcon={<LockIcon />}
      />
    </EnhancedForm>
  );
};
